/**
 * DeactivatePopupModal — Sprint 4 F5a-3
 *
 * Backend:
 *   POST /api/organizasyon/personnel/{id}/deactivate-preview
 *   POST /api/organizasyon/personnel/{id}/deactivate-auto
 *
 * Akış:
 *   1. Modal açılır → preview fetch (alt personel sayısı, önerilen yeni amir,
 *      bekleyen onay sayısı).
 *   2. Kullanıcı "AUTO" veya "İptal" seçer.
 *   3. AUTO → POST /deactivate-auto → çocuklar parent'a cascade,
 *      pending approval'lar redirect, kişi soft-delete.
 *   4. Tamamlanınca onComplete() çağrılır (parent listesi yenilenir).
 *
 * NOT: Manuel mode (her çocuğa elle yeni amir atama) Sprint 5'te genişletilir.
 * Şu an sadece AUTO mode. Manuel için preview göstereyip "İptal" + ayrı menüden
 * personel düzenleme yönlendirir.
 */
import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle, Users, ArrowDownToLine, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

import apiClient from "@/api/client";


export default function DeactivatePopupModal({
  personnelId,
  personnelName,
  onClose,
  onComplete,
}) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);
    apiClient
      .post(`/organizasyon/personnel/${personnelId}/deactivate-preview`)
      .then((res) => {
        if (mounted) setPreview(res.data);
      })
      .catch((err) => {
        if (mounted) {
          const msg = err?.response?.data?.error?.message || err.message;
          setError(msg);
          toast.error("Önizleme alınamadı: " + msg);
        }
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [personnelId]);

  const handleAutoDeactivate = async () => {
    setExecuting(true);
    try {
      const res = await apiClient.post(
        `/organizasyon/personnel/${personnelId}/deactivate-auto`
      );
      const result = res.data;
      toast.success(
        `'${personnelName}' pasifleştirildi. ` +
        `${result.reassigned_children} alt personel yeni amire bağlandı. ` +
        `${result.rerouted_approvals} bekleyen onay yönlendirildi.`,
        { duration: 6000 }
      );
      onComplete?.(result);
    } catch (err) {
      const msg = err?.response?.data?.error?.message || err.message;
      toast.error("Pasifleştirme başarısız: " + msg);
    } finally {
      setExecuting(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose?.()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="h-5 w-5" />
            Personeli Pasifleştir: {personnelName}
          </DialogTitle>
          <DialogDescription>
            Bu işlem geri alınamaz. Alt personel ve bekleyen onaylar otomatik
            olarak bir üst amire devredilir.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {loading && (
            <div className="text-center py-6">
              <Loader2 className="h-6 w-6 animate-spin mx-auto text-gray-400" />
              <p className="text-sm text-gray-500 mt-2">Önizleme yükleniyor...</p>
            </div>
          )}

          {error && (
            <Alert className="bg-red-50 border-red-200">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-red-800">{error}</AlertDescription>
            </Alert>
          )}

          {preview && !loading && (
            <>
              {/* Bilgi kutuları */}
              <div className="grid grid-cols-2 gap-3">
                <div className="border rounded p-3 bg-orange-50 border-orange-200">
                  <div className="flex items-center gap-2 text-orange-700 text-sm font-semibold mb-1">
                    <Users className="h-4 w-4" />
                    Alt Personel
                  </div>
                  <div className="text-2xl font-bold text-orange-700">
                    {preview.children_count}
                  </div>
                  <div className="text-xs text-orange-600">
                    Yeni amire devredilecek
                  </div>
                </div>
                <div className="border rounded p-3 bg-blue-50 border-blue-200">
                  <div className="flex items-center gap-2 text-blue-700 text-sm font-semibold mb-1">
                    <ArrowDownToLine className="h-4 w-4" />
                    Bekleyen Onay
                  </div>
                  <div className="text-2xl font-bold text-blue-700">
                    {preview.pending_approvals_count}
                  </div>
                  <div className="text-xs text-blue-600">
                    Otomatik yeniden yönlendirilecek
                  </div>
                </div>
              </div>

              {/* Önerilen yeni amir */}
              {preview.suggested_parent_id && (
                <div className="border rounded p-3 bg-green-50 border-green-200">
                  <div className="text-sm font-semibold text-green-700 mb-1">
                    Önerilen Yeni Amir (auto-mode)
                  </div>
                  <div className="text-base font-medium">
                    {preview.suggested_parent_name}
                  </div>
                  <div className="text-xs text-green-600 mt-1">
                    Alt personelin tamamı bu kişiye bağlanacak.
                  </div>
                </div>
              )}

              {!preview.suggested_parent_id && preview.children_count > 0 && (
                <Alert className="bg-amber-50 border-amber-200">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="text-amber-800">
                    <strong>UYARI:</strong> Bu personelin üst amiri (parent) yok.
                    Pasifleştirildiğinde alt personel orphan kalacak — manuel
                    atama gerekecek.
                  </AlertDescription>
                </Alert>
              )}

              {/* Alt personel listesi (ilk 10) */}
              {preview.children_count > 0 && (
                <div className="border rounded p-3 max-h-40 overflow-y-auto">
                  <div className="text-xs font-semibold text-gray-700 mb-2">
                    Etkilenecek Alt Personel (ilk {Math.min(10, preview.children_count)}):
                  </div>
                  <div className="space-y-1">
                    {(preview.children || []).slice(0, 10).map((c) => (
                      <div
                        key={c.id}
                        className="flex items-center gap-2 text-xs"
                      >
                        <Badge variant="outline" className="text-xs">
                          {c.role}
                        </Badge>
                        <span>{c.name}</span>
                      </div>
                    ))}
                    {preview.children_count > 10 && (
                      <div className="text-xs text-gray-500 italic">
                        ... ve {preview.children_count - 10} kişi daha.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={executing}
          >
            İptal
          </Button>
          <Button
            onClick={handleAutoDeactivate}
            disabled={executing || loading || !preview}
            className="bg-red-600 hover:bg-red-700"
          >
            {executing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Pasifleştiriliyor...
              </>
            ) : (
              <>
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Auto Mode ile Pasifleştir
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
